import AsyncStorage from "@/platform/async-storage";
import * as FileSystem from "@/platform/file-system";
import { peekApiSessionGen, tenantHashDaSessao } from "./Api";

/**
 * Catálogos do tenant (postos, veículos, pessoas, checklists, bancos) em ARQUIVO.
 *
 * Por quê (05/10/2026): no Android o AsyncStorage (SQLite) grava qualquer tamanho, mas não
 * LÊ de volta um valor maior que a janela do cursor (~2 MB): "Row too big to fit into
 * CursorWindow". O t-credentials manda essas listas inteiras, sem limite — na REAL JG a lista
 * de pessoas passou de 2 MB. A leitura no boot lançava e levava o token junto: o app reabria
 * deslogado e pedia o CPF a cada abertura. Arquivo não tem esse limite.
 *
 * Formato: `{ hash, rows }`. `hash` = tenant da sessão (claim c_hash do token): arquivo de OUTRA
 * empresa é descartado na leitura (ids colidem entre bancos — mesma regra do userData.json).
 * É só cache: falhar em ler ou gravar nunca pode quebrar nada, quem chama segue sem a lista.
 */
export type ListaCache = "postos" | "veiculos" | "pessoas" | "checklists" | "bancos";

const LISTAS: ListaCache[] = ["postos", "veiculos", "pessoas", "checklists", "bancos"];

/** Onde a lista morava até 05/10/2026 — migrada e apagada na primeira leitura. */
const CHAVE_ANTIGA: Record<ListaCache, string> = {
  postos: "@tirvumais:postos",
  veiculos: "@tirvumais:veiculos",
  pessoas: "@tirvumais:pessoas",
  checklists: "@tirvumais:checklists",
  bancos: "@tirvumais:bancos_v2",
};

const DIR = FileSystem.documentDirectory + "tirvumais_data/";
const arquivo = (lista: ListaCache) => `${DIR}cache_${lista}.json`;

const hashDaSessao = async (): Promise<string> =>
  String(await tenantHashDaSessao().catch(() => ""))
    .trim()
    .toUpperCase();

const apagarArquivo = async (caminho: string) => {
  await FileSystem.deleteAsync(caminho, { idempotent: true }).catch(() => {});
};

/**
 * UMA operação por lista de cada vez. Leitura e gravação da MESMA lista ao mesmo tempo (o
 * refreshStaticData e o ensureChecklistsLoaded podem coincidir) disputavam o mesmo temporário
 * e podiam terminar sem arquivo nenhum. Em fila, a última gravação vence — como no AsyncStorage.
 */
const filaPorLista = new Map<ListaCache, Promise<unknown>>();
const naFila = <T>(lista: ListaCache, operacao: () => Promise<T>): Promise<T> => {
  const anterior = filaPorLista.get(lista) ?? Promise.resolve();
  const atual = anterior.catch(() => {}).then(operacao);
  filaPorLista.set(lista, atual);
  return atual;
};

/** Leitura que falhou porque o valor não cabe na janela do cursor do Android. */
const ehErroDeTamanho = (e: unknown) =>
  /CursorWindow|Couldn't read row/i.test(String((e as any)?.message ?? e));

/**
 * Tira a lista da chave antiga do AsyncStorage (versões até 4.0.22). Legível → vira o arquivo e
 * a chave sai. Grande demais para ler (o valor que derrubava o boot) → só apaga: `removeItem` é
 * um DELETE, sem cursor, e funciona mesmo assim. Outro erro (passageiro) → a chave fica para a
 * próxima abertura tentar de novo.
 */
const migrarChaveAntiga = async (lista: ListaCache): Promise<any[] | null> => {
  const chave = CHAVE_ANTIGA[lista];
  const geracao = peekApiSessionGen();
  let bruto: string | null = null;
  try {
    bruto = await AsyncStorage.getItem(chave);
  } catch (e) {
    if (ehErroDeTamanho(e)) {
      console.warn(`[CacheListas] ${lista}: chave antiga grande demais para ler — apagada.`);
      await AsyncStorage.removeItem(chave).catch(() => {});
    } else {
      console.warn(`[CacheListas] ${lista}: chave antiga não lida (tenta na próxima abertura):`, e);
    }
    return null;
  }
  if (bruto == null) return null;
  let rows: any[] | null = null;
  try {
    const parsed = JSON.parse(bruto);
    if (Array.isArray(parsed)) rows = parsed;
  } catch {}
  // Corrompida: não há o que salvar. Legível: só sai depois de gravada no arquivo.
  if (!rows || (await gravarSemFila(lista, rows, geracao))) {
    await AsyncStorage.removeItem(chave).catch(() => {});
  }
  return rows;
};

/** A lista guardada, ou null (sem cache, ilegível ou de outra empresa). Nunca lança. */
export function lerListaCache(lista: ListaCache): Promise<any[] | null> {
  return naFila(lista, async () => {
    let texto: string;
    try {
      const info = await FileSystem.getInfoAsync(arquivo(lista));
      if (!info.exists) return await migrarChaveAntiga(lista);
      texto = await FileSystem.readAsStringAsync(arquivo(lista));
    } catch (e) {
      // Erro de I/O: devolve sem apagar — o arquivo pode estar bom na próxima leitura.
      console.warn(`[CacheListas] ${lista}: leitura falhou (segue sem cache):`, e);
      return null;
    }
    try {
      const conteudo = JSON.parse(texto);
      const rows = Array.isArray(conteudo?.rows) ? conteudo.rows : null;
      const hashArq = String(conteudo?.hash ?? "")
        .trim()
        .toUpperCase();
      const hashSessao = await hashDaSessao();
      if (rows && !(hashArq && hashSessao && hashArq !== hashSessao)) return rows;
    } catch {}
    // Corrompido, formato inesperado ou de outra empresa: descarta.
    await apagarArquivo(arquivo(lista));
    return null;
  });
}

const gravarSemFila = async (
  lista: ListaCache,
  rows: any[],
  geracao: number,
): Promise<boolean> => {
  const destino = arquivo(lista);
  const temp = `${destino}.tmp`;
  try {
    // Hash lido e geração conferida ANTES de montar o conteúdo: numa troca de empresa no meio,
    // a lista da anterior sairia carimbada com o hash da nova.
    const hash = await hashDaSessao();
    if (peekApiSessionGen() !== geracao) return false;
    await FileSystem.makeDirectoryAsync(DIR, { intermediates: true });
    await FileSystem.writeAsStringAsync(temp, JSON.stringify({ hash, rows }));
    if (peekApiSessionGen() !== geracao) {
      await apagarArquivo(temp);
      return false;
    }
    // A troca substitui o destino (rename no Android; o próprio moveAsync remove o anterior no
    // iOS): o arquivo nunca fica pela metade nem some entre uma versão e outra.
    await FileSystem.moveAsync({ from: temp, to: destino });
    return true;
  } catch (e) {
    console.warn(`[CacheListas] ${lista}: gravação falhou (segue sem cache):`, e);
    await apagarArquivo(temp);
    return false;
  }
};

/**
 * Grava a lista (com o tenant da sessão). Nunca lança; devolve se gravou.
 * `geracao` (peekApiSessionGen() capturado por quem BUSCOU a lista): se a sessão mudou no meio
 * (signOut, troca de empresa), não grava.
 */
export function gravarListaCache(
  lista: ListaCache,
  rows: any[],
  geracao: number = peekApiSessionGen(),
): Promise<boolean> {
  return naFila(lista, () => gravarSemFila(lista, rows, geracao));
}

/** Apaga todas as listas (arquivos e chaves antigas) — signIn, signOut, clearAuth. Nunca lança. */
export async function apagarListasCache(): Promise<void> {
  await Promise.all(
    LISTAS.flatMap((lista) => [
      apagarArquivo(arquivo(lista)),
      apagarArquivo(`${arquivo(lista)}.tmp`),
      AsyncStorage.removeItem(CHAVE_ANTIGA[lista]).catch(() => {}),
    ]),
  );
}
